import {
  UPSELL_STORAGE_KEYS,
  getStorageItem,
  setStorageItem,
  getStorageJSON,
  setStorageJSON,
} from "~/utils/upsell-storage-utils";

// Timing rules configuration
export const TIMING_RULES = {
  minDaysAfterPurchase: 3,
  cooldownBetweenShowsDays: 2,
  dismissalSnoozePeriodDays: 14,
} as const;

// Trigger thresholds
export const TRIGGER_THRESHOLDS = {
  useCaseClicks: 5,
  contentConsumed: 5,
  sandboxPromptsInTwoDays: 3,
} as const;

// Trigger event types
export interface TriggerEvent {
  type:
    | "use_case_click"
    | "content_consumed"
    | "sandbox_prompt"
    | "ai_usage_log"
    | "use_case_to_sandbox";
  timestamp: number;
  metadata?: Record<string, any>;
}

// Dismissed triggers tracking
export interface DismissedTriggers {
  [triggerType: string]: number; // timestamp of dismissal
}

// Sandbox prompts data structure
export interface SandboxPromptsData {
  prompts: Array<{
    timestamp: number;
  }>;
}

// Check if user is eligible for upsell modal
export function checkUserEligibility(
  hasActiveSubscription: boolean,
  isEnrolledInProgram: boolean,
): boolean {
  return hasActiveSubscription && !isEnrolledInProgram;
}

// Check timing rules to see if modal can be shown
export function checkTimingRules(purchaseDate?: string): boolean {
  const now = Date.now();

  // Check minimum days after purchase
  if (purchaseDate) {
    const purchaseTime = new Date(purchaseDate).getTime();
    const daysSincePurchase = (now - purchaseTime) / (1000 * 60 * 60 * 24);
    if (daysSincePurchase < TIMING_RULES.minDaysAfterPurchase) {
      return false;
    }
  }

  // Check cooldown between shows
  const lastShown = getStorageItem(UPSELL_STORAGE_KEYS.LAST_UPSELL_SHOWN);
  if (lastShown) {
    const lastShownTime = parseInt(lastShown, 10);
    const daysSinceLastShown = (now - lastShownTime) / (1000 * 60 * 60 * 24);
    if (daysSinceLastShown < TIMING_RULES.cooldownBetweenShowsDays) {
      return false;
    }
  }

  // Check dismissal snooze period
  const dismissedTriggers = getStorageJSON<DismissedTriggers>(
    UPSELL_STORAGE_KEYS.DISMISSED_TRIGGERS,
    {},
  );

  for (const dismissalTime of Object.values(dismissedTriggers)) {
    const daysSinceDismissal = (now - dismissalTime) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissal < TIMING_RULES.dismissalSnoozePeriodDays) {
      return false;
    }
  }

  return true;
}

// Track use case click
export function trackUseCaseClick(): void {
  const count = parseInt(
    getStorageItem(UPSELL_STORAGE_KEYS.USE_CASE_CLICK_COUNT, "0"),
    10,
  );
  const newCount = count + 1;
  setStorageItem(UPSELL_STORAGE_KEYS.USE_CASE_CLICK_COUNT, newCount.toString());

  // Check if threshold reached and trigger recheck
  if (newCount >= TRIGGER_THRESHOLDS.useCaseClicks) {
    import("./mindstone-plus-upsell-context").then(
      ({ triggerUpsellRecheck }) => {
        triggerUpsellRecheck();
      },
    );
  }
}

// Check if use case click trigger should fire
export function checkUseCaseClickTrigger(): boolean {
  const count = parseInt(
    getStorageItem(UPSELL_STORAGE_KEYS.USE_CASE_CLICK_COUNT, "0"),
    10,
  );
  return count >= TRIGGER_THRESHOLDS.useCaseClicks;
}

// Track content consumption
export function trackContentConsumed(): void {
  const count = parseInt(
    getStorageItem(UPSELL_STORAGE_KEYS.CONTENT_CONSUMED_COUNT, "0"),
    10,
  );
  const newCount = count + 1;
  setStorageItem(
    UPSELL_STORAGE_KEYS.CONTENT_CONSUMED_COUNT,
    newCount.toString(),
  );

  // Check if threshold reached and trigger recheck
  if (newCount >= TRIGGER_THRESHOLDS.contentConsumed) {
    import("./mindstone-plus-upsell-context").then(
      ({ triggerUpsellRecheck }) => {
        triggerUpsellRecheck();
      },
    );
  }
}

// Check if content consumption trigger should fire
export function checkContentConsumedTrigger(): boolean {
  const count = parseInt(
    getStorageItem(UPSELL_STORAGE_KEYS.CONTENT_CONSUMED_COUNT, "0"),
    10,
  );
  return count >= TRIGGER_THRESHOLDS.contentConsumed;
}

// Track sandbox prompt
export function trackSandboxPrompt(): void {
  const data = getStorageJSON<SandboxPromptsData>(
    UPSELL_STORAGE_KEYS.SANDBOX_PROMPTS_DATA,
    { prompts: [] },
  );

  data.prompts.push({
    timestamp: Date.now(),
  });

  setStorageJSON(UPSELL_STORAGE_KEYS.SANDBOX_PROMPTS_DATA, data);

  // Check if threshold reached and trigger recheck
  const twoDaysAgo = Date.now() - 2 * 24 * 60 * 60 * 1000;
  const recentPrompts = data.prompts.filter((p) => p.timestamp > twoDaysAgo);

  if (recentPrompts.length >= TRIGGER_THRESHOLDS.sandboxPromptsInTwoDays) {
    import("./mindstone-plus-upsell-context").then(
      ({ triggerUpsellRecheck }) => {
        triggerUpsellRecheck();
      },
    );
  }
}

// Check if sandbox prompt trigger should fire (3 prompts in 2 days)
export function checkSandboxPromptTrigger(): boolean {
  const data = getStorageJSON<SandboxPromptsData>(
    UPSELL_STORAGE_KEYS.SANDBOX_PROMPTS_DATA,
    { prompts: [] },
  );

  const twoDaysAgo = Date.now() - 2 * 24 * 60 * 60 * 1000;
  const recentPrompts = data.prompts.filter((p) => p.timestamp > twoDaysAgo);

  return recentPrompts.length >= TRIGGER_THRESHOLDS.sandboxPromptsInTwoDays;
}

// Track use case to sandbox navigation
export function trackUseCaseToSandboxNavigation(): void {
  const lastVisit = getStorageItem(UPSELL_STORAGE_KEYS.LAST_USE_CASE_VISIT);

  if (lastVisit) {
    const lastVisitTime = parseInt(lastVisit, 10);
    const now = Date.now();
    const timeDiff = now - lastVisitTime;
    const minutesDiff = Math.round(timeDiff / (1000 * 60));

    // If navigated to sandbox within 5 minutes of use case visit, set the trigger
    if (timeDiff < 5 * 60 * 1000) {
      setStorageItem(
        UPSELL_STORAGE_KEYS.USE_CASE_TO_SANDBOX_TRIGGERED,
        now.toString(),
      );
      // Clear the use case visit timestamp since we've processed it
      setStorageItem(UPSELL_STORAGE_KEYS.LAST_USE_CASE_VISIT, "");

      // Trigger immediate recheck of upsell modal
      // Import the function dynamically to avoid circular dependencies
      import("./mindstone-plus-upsell-context").then(
        ({ triggerUpsellRecheck }) => {
          triggerUpsellRecheck();
        },
      );
    }
  }
}

// Track use case visit (for navigation tracking)
export function trackUseCaseVisit(): void {
  const now = Date.now();
  setStorageItem(UPSELL_STORAGE_KEYS.LAST_USE_CASE_VISIT, now.toString());
}

// Check if use case to sandbox navigation trigger should fire
export function checkUseCaseToSandboxTrigger(): boolean {
  const triggered = getStorageItem(
    UPSELL_STORAGE_KEYS.USE_CASE_TO_SANDBOX_TRIGGERED,
  );
  if (!triggered) return false;

  // If trigger is set, it should fire (it gets cleared when modal is shown or dismissed)
  return true;
}

// Clear active triggers (called when modal is shown or dismissed)
export function clearActiveTriggers(): void {
  setStorageItem(UPSELL_STORAGE_KEYS.USE_CASE_TO_SANDBOX_TRIGGERED, "");
}

// Mark modal as shown
export function markModalShown(): void {
  setStorageItem(UPSELL_STORAGE_KEYS.LAST_UPSELL_SHOWN, Date.now().toString());
  // Clear any active triggers since modal was shown
  clearActiveTriggers();
}

// Mark trigger as dismissed
export function markTriggerDismissed(triggerType: string): void {
  const dismissedTriggers = getStorageJSON<DismissedTriggers>(
    UPSELL_STORAGE_KEYS.DISMISSED_TRIGGERS,
    {},
  );

  dismissedTriggers[triggerType] = Date.now();
  setStorageJSON(UPSELL_STORAGE_KEYS.DISMISSED_TRIGGERS, dismissedTriggers);

  // Clear any active triggers since user dismissed
  clearActiveTriggers();
}

// Check all triggers and return the first one that should fire
export function checkAndGetActiveTrigger(): string | null {
  const useCaseClick = checkUseCaseClickTrigger();
  if (useCaseClick) return "use_case_click";

  const contentConsumed = checkContentConsumedTrigger();
  if (contentConsumed) return "content_consumed";

  const sandboxPrompt = checkSandboxPromptTrigger();
  if (sandboxPrompt) return "sandbox_prompt";

  const useCaseToSandbox = checkUseCaseToSandboxTrigger();
  if (useCaseToSandbox) return "use_case_to_sandbox";

  return null;
}
